
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, XCircle, Upload, AlertCircle } from 'lucide-react';
import { useLocalShopExcelUpload } from './useLocalShopExcelUpload';
import LocalShopUploadTemplate from './LocalShopUploadTemplate';
import LocalShopPreviewTable from './LocalShopPreviewTable';

interface LocalShopUploadNewProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const LocalShopUploadNew: React.FC<LocalShopUploadNewProps> = ({ onSuccess, onCancel }) => {
  const upload = useLocalShopExcelUpload();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      upload.processFile(file);
    }
  };

  const handleUpload = async () => {
    await upload.uploadData();
    setTimeout(() => {
      onSuccess();
    }, 2000);
  };

  const getProgressValue = () => {
    if (upload.step === 'complete') return 100;
    if (upload.step === 'uploading' && upload.uploadProgress.total > 0) {
      return (upload.uploadProgress.current / upload.uploadProgress.total) * 100;
    }
    return 0;
  };

  return (
    <div className="space-y-6">
      <LocalShopUploadTemplate />

      {upload.step === 'select' && (
        <div className="space-y-4">
          <div>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              disabled={upload.isProcessing}
            />
            {upload.selectedFile && (
              <p className="text-sm text-gray-600 mt-2">
                Selected: {upload.selectedFile.name}
              </p>
            )}
          </div>

          {upload.isProcessing && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Processing file...</AlertDescription>
            </Alert>
          )}

          {upload.hasErrors && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-1">
                  <p className="font-medium">Please fix the following errors:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {upload.parseErrors.map((error, index) => (
                      <li key={index} className="text-sm">{error}</li>
                    ))}
                  </ul>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {upload.step === 'preview' && (
        <div className="space-y-4">
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              Successfully parsed {upload.parsedData.length} local shop prices. Review the data below before uploading.
            </AlertDescription>
          </Alert>

          <LocalShopPreviewTable data={upload.parsedData} />

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={upload.reset}>
              Start Over
            </Button>
            <Button onClick={handleUpload}>
              <Upload className="h-4 w-4 mr-2" />
              Upload {upload.parsedData.length} Prices
            </Button>
          </div>
        </div>
      )}

      {upload.step === 'uploading' && (
        <div className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>Uploading local shop prices...</AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Progress value={getProgressValue()} />
            <p className="text-sm text-gray-600 text-center">
              {upload.uploadProgress.current} of {upload.uploadProgress.total} processed
            </p>
          </div>
        </div>
      )}

      {upload.step === 'complete' && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            Successfully uploaded {upload.parsedData.length} local shop prices!
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default LocalShopUploadNew;
